/**
 * DistributionChart — Generic donut chart for arbitrary segments.
 * Accepts data=[{name, value, color}], title, centerLabel.
 */

import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

const DistributionChart = ({ data = [], title, centerLabel }) => {
  const filtered = data.filter(d => d.value > 0);
  const total = filtered.reduce((sum, d) => sum + d.value, 0);

  if (total === 0) {
    return (
      <div className="card p-6 flex items-center justify-center h-full">
        <p className="text-sm text-gray-400">No data available</p>
      </div>
    );
  }

  return (
    <div className="card p-5 h-full">
      {title && <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>}
      <div className="flex items-center">
        <div className="w-3/5" role="img" aria-label={`${title || 'Distribution'} chart showing ${filtered.map(d => `${d.name}: ${d.value}`).join(', ')}`}>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={filtered}
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={72}
                paddingAngle={2}
                dataKey="value"
              >
                {filtered.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Pie>
              <text x="50%" y="45%" textAnchor="middle" dominantBaseline="central" fill="#374151" fontSize="22" fontWeight="bold">
                {total}
              </text>
              <text x="50%" y="60%" textAnchor="middle" dominantBaseline="central" fill="#9ca3af" fontSize="11">
                {centerLabel || 'total'}
              </text>
              <Tooltip formatter={(value) => [value, 'Count']} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="w-2/5 space-y-2 pl-2">
          {filtered.map((item) => (
            <div key={item.name} className="flex items-center space-x-2">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <div className="min-w-0">
                <p className="text-xs text-gray-500">{item.name}</p>
                <p className="text-sm font-semibold text-gray-800">
                  {item.value}
                  <span className="text-xs font-normal text-gray-400 ml-1">
                    ({Math.round((item.value / total) * 100)}%)
                  </span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default React.memo(DistributionChart);
